import type { ReactNode } from "react";
import React from "react";
import {
  type ErrorResponse,
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from "react-router-dom";
import { Button } from "@arenko-group/arenko-ui";
import useTitle from "@/hooks/useTitle";
import css from "./ErrorPage.module.css";

type RouterError = string | Error | ErrorResponse;

const ErrorPage = () => {
  const error = useRouteError() as RouterError;
  const navigate = useNavigate();

  useTitle("Navigation error");

  let heading: ReactNode = "Oops!";
  let body: ReactNode = <p>Sorry, an unknown error has occurred.</p>;
  const contact = (
    <>
      Please{" "}
      <a
        href="https://help.nimbus.arenko.group"
        target="_blank"
        rel="noopener noreferrer"
      >
        contact support
      </a>{" "}
      if you need further help.
    </>
  );

  if (isRouteErrorResponse(error)) {
    heading = "Not found";
    body = (
      <>
        <p>
          Sorry, the page doesn&apos;t exist or you do not have permission to
          view it.
          <br />
        </p>
        <p>{contact}</p>
      </>
    );
  } else if (error instanceof Error) {
    body = (
      <>
        <p>
          Sorry, an error has occurred:
          <i>{error.message}</i>
        </p>
        <p>{contact}</p>
      </>
    );
  } else if (typeof error === "string") {
    body = (
      <>
        <p>
          Sorry, an error has occurred:
          <i>{error}</i>
        </p>
        <p>{contact}</p>
      </>
    );
  }

  return (
    <main id="app-main">
      <div className={css.errorPageContainer}>
        <h1>{heading}</h1>
        {body}
        <div className={css.backButton}>
          <Button
            variant="contained"
            onClick={() => {
              navigate(-1);
            }}
          >
            &larr; Go back
          </Button>
        </div>
      </div>
    </main>
  );
};

export default ErrorPage;
